import { Dispatch, SetStateAction, useState } from 'react';
import { Contact } from '@weave/schema-gen-ts/dist/schemas/phone/contacts/contacts/contacts.pb';
import { useTranslation } from '@frontend/i18n';
import {
  EditSimpleIcon,
  ModalControlTriggerProps,
  PlusIcon,
  TrashIcon,
  useModalControl,
  UserManagementIcon,
} from '@frontend/design-system';
import { DeleteContactModal } from '../modals/delete-contacts';
import { EditContactModal } from '../modals/edit-contact';
import { NewContactModal } from '../modals/new-contact';
import type { SelectedContactsType } from '../types';
import { ContactsTable } from './contacts';

type Props = ModalControlTriggerProps & {
  setAssignedContacts: Dispatch<SetStateAction<Contact[]>>;
};

export const AllContactsTable = ({ onClick: openCreateNewDirectoryModal, setAssignedContacts }: Props) => {
  const { t } = useTranslation('phone', { keyPrefix: 'contact-directories' });
  const { modalProps: editContactModalProps, triggerProps: editContactTriggerProps } = useModalControl();
  const { modalProps: deleteContactModalProps, triggerProps: deleteContactTriggerProps } = useModalControl();
  const { modalProps: newContactModalProps, triggerProps: newContactTriggerProps } = useModalControl();

  const [selectedContacts, setSelectedContacts] = useState<SelectedContactsType>({
    contacts: [],
    contactNumber: '',
  });

  return (
    <>
      <ContactsTable
        fullHeightConfig={500}
        emptyStateConfig={{
          type: 'contact_directories',
          header: t('No Contacts'),
          description: t(
            'Create contact lists of your business contacts and view all contacts from each list in this tab.'
          ),
          action: {
            label: t('Add New List'),
            onClick: openCreateNewDirectoryModal,
            trackingId: 'add-new-contact-list',
          },
        }}
        bulkActions={[
          {
            Icon: PlusIcon,
            label: t('Add to New List'),
            onClick: (_, selectedContacts) => {
              setAssignedContacts(selectedContacts);
              openCreateNewDirectoryModal();
            },
          },
          {
            Icon: TrashIcon,
            label: t('Delete Contacts'),
            destructive: true,
            onClick: (_, selectedContacts) => {
              setSelectedContacts({
                contacts: selectedContacts,
              });
              deleteContactTriggerProps.onClick();
            },
          },
        ]}
        tableActions={[
          {
            label: t('Add Contacts'),
            Icon: UserManagementIcon,
            type: 'icon',
            onClick: newContactTriggerProps.onClick,
            disabled: selectedContacts.contacts.length > 0,
          },
        ]}
        rowActions={[
          {
            actions: [
              {
                label: t('Edit Contact'),
                Icon: EditSimpleIcon,
                onClick: (contact) => {
                  setSelectedContacts({
                    contacts: [contact],
                  });
                  editContactTriggerProps.onClick();
                },
              },
              {
                label: t('Delete Contact'),
                Icon: TrashIcon,
                destructive: true,
                onClick: (contact) => {
                  setSelectedContacts({
                    contacts: [contact],
                  });
                  deleteContactTriggerProps.onClick();
                },
              },
            ],
          },
        ]}
        rowSelectionConfig={{
          onSelectionToggle: (_, contacts) => {
            setSelectedContacts({
              contacts,
            });
          },
        }}
      />
      <NewContactModal {...newContactModalProps} />
      <EditContactModal selectedContacts={selectedContacts} {...editContactModalProps} />
      <DeleteContactModal selectedContacts={selectedContacts} {...deleteContactModalProps} />
    </>
  );
};
